import { AppImage } from '@/components/ui/app-image';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import type { ImageSize } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useState } from 'react';
import { View, type StyleProp, type ViewStyle } from 'react-native';

/** At or above this rendered size (pt) the larger `avatar` preset is used. */
const AVATAR_PRESET_MIN_SIZE = 64;

/**
 * URIs that point at the device (a just-picked photo) rather than R2. They go
 * to expo-image unchanged: `imageUrl` would treat them as R2 keys.
 */
const LOCAL_URI = /^(file|content|data|blob|ph|assets-library):/;

export interface UserAvatarProps {
  /** R2 key, full URL (transformed or not), or a local file URI. */
  uri: string | null | undefined;
  /** Display name: drives the initials fallback and the default label. */
  name?: string | null;
  /** Diameter in points. */
  size: number;
  /** Overrides the default "<name>'s profile photo" label. */
  accessibilityLabel?: string;
  /** Hide from assistive tech when a parent already names the person. */
  decorative?: boolean;
  /** Called once when the image fails to load (the fallback shows anyway). */
  onError?: () => void;
  /** Extra classes for the circular container (borders, background). */
  className?: string;
  /** Extra style for the circular container (margins, zIndex). */
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/** First letters of the first two words, uppercased. Empty when there is no name. */
export function getInitials(name?: string | null): string {
  const trimmed = name?.trim();
  if (!trimmed) return '';
  return trimmed
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}

function presetFor(size: number): ImageSize {
  return size >= AVATAR_PRESET_MIN_SIZE ? 'avatar' : 'thumbnail';
}

function defaultLabel(name?: string | null): string {
  const trimmed = name?.trim();
  return trimmed ? `${trimmed}'s profile photo` : 'Profile photo';
}

/**
 * The app's one avatar: a circle showing the user's photo through `AppImage`
 * (thumbnail preset under 64pt, avatar preset at 64pt and up), falling back to
 * initials and then a person icon. Never renders nothing.
 */
export function UserAvatar({
  uri,
  name,
  size,
  accessibilityLabel,
  decorative = false,
  onError,
  className,
  style,
  testID,
}: UserAvatarProps) {
  // Track the uri that failed, not a boolean, so a new uri gets a fresh try.
  const [failedUri, setFailedUri] = useState<string | null>(null);
  const showImage = !!uri && failedUri !== uri;
  const label = accessibilityLabel ?? defaultLabel(name);

  const handleError = () => {
    setFailedUri(uri ?? null);
    onError?.();
  };

  const containerA11y = showImage || decorative
    ? { accessible: false }
    : { accessible: true, accessibilityRole: 'image' as const, accessibilityLabel: label };

  return (
    <View
      testID={testID}
      className={cn('items-center justify-center bg-surface-secondary', className)}
      style={[
        { width: size, height: size, borderRadius: size / 2, overflow: 'hidden' },
        style,
      ]}
      {...containerA11y}
    >
      {showImage ? (
        <AvatarImage
          uri={uri}
          size={size}
          label={label}
          decorative={decorative}
          onError={handleError}
        />
      ) : (
        <AvatarFallback name={name} size={size} />
      )}
    </View>
  );
}

function AvatarImage({
  uri,
  size,
  label,
  decorative,
  onError,
}: {
  uri: string;
  size: number;
  label: string;
  decorative: boolean;
  onError: () => void;
}) {
  const imageStyle = { width: '100%' as const, height: '100%' as const, borderRadius: size / 2 };
  const a11y = decorative
    ? ({ decorative: true } as const)
    : ({ accessibilityLabel: label } as const);

  if (LOCAL_URI.test(uri)) {
    return (
      <AppImage
        source={{ uri }}
        cachePolicy="none"
        style={imageStyle}
        onError={onError}
        {...a11y}
      />
    );
  }

  return (
    <AppImage
      r2Key={uri}
      preset={presetFor(size)}
      recyclingKey={uri}
      style={imageStyle}
      onError={onError}
      {...a11y}
    />
  );
}

function AvatarFallback({ name, size }: { name?: string | null; size: number }) {
  const initials = getInitials(name);
  if (initials) {
    return (
      <Text
        className="font-semibold text-muted-foreground"
        style={{ fontSize: Math.max(8, Math.round(size * 0.38)) }}
        accessible={false}
      >
        {initials}
      </Text>
    );
  }
  return (
    <Ionicons
      name="person"
      size={Math.round(size * 0.5)}
      className="text-muted-foreground"
      accessible={false}
    />
  );
}
